<!-- components/Comunes/SeccionInformativaSplit.vue -->
<!--
  Sección Informativa Split (reutilizable, presentacional y pixel-perfect)

  Objetivo UX/UI:
  - Mobile: texto arriba + media abajo, SIN que la imagen superpuesta invada el texto.
    => el panel media RECORTA (overflow-hidden).
  - Desktop: split 50/50 y la imagen superpuesta PUEDE cruzar al panel izquierdo.
    => el panel media permite overflow (md:overflow-visible), pero el root recorta por radio.

  Restricciones:
  - Un solo componente (sin archivos adicionales).
  - Backward compatible con props legacy.
-->

<template>

  <section class="w-full py-8" :class="clase_seccion" role="region" :aria-label="aria_label_efectivo">
    <div :class="[
      usar_contenedor ? 'mx-auto w-full max-w-[1440px] px-4 md:px-6' : '',
      clase_contenedor
    ]">
      <!-- Root card: SIEMPRE recorta para respetar border-radius -->
      <div class="seccion_informativa_split__root w-full overflow-hidden" :style="estilo_root">
        <div :class="['w-full h-full flex flex-col md:grid md:grid-cols-2', clase_grid]">
          <!-- ======================== PANEL IZQUIERDO: TEXTO ========================== -->
          <div :class="[
            // z-0 para que en desktop la imagen del panel derecho pueda superponerse por encima
            'relative z-0 text-white bg-ROJO_LEON',
            // Padding mobile (legacy: 30px 20px aprox.)
            'px-5 pt-7 pb-8',
            // Padding desktop (legacy: 70px 130px)
            'md:px-[90px] md:pt-[70px] md:pb-[70px] md:h-full',
            // Fondo legacy como cover
            'bg-cover bg-no-repeat bg-left-top',
            clase_panel_texto
          ]" :style="estilo_fondo_texto">
            <div class="relative z-10">
              <header class="flex items-start gap-3">
                <span class="mt-[6px] inline-block h-0 w-0 flex-none" :style="estilo_triangulo" aria-hidden="true" />
                <h2 :class="['uppercase font-semibold text-[28px] leading-[1.05] md:text-[34px] tracking-[0.5px]',
                  clase_titulo
                ]">
                  <slot name="titulo">
                    {{ titulo }}
                  </slot>
                </h2>
              </header>

              <div :class="['mt-5 space-y-4 md:mt-6', clase_contenido_texto]">
                <slot name="contenido">
                  <p v-for="(parrafo, idx) in parrafos_normalizados" :key="idx" 
                    :class="['text-white/95 text-[14px] leading-[1.6]',
                    // Evita desbordes raros en textos largos
                    'break-words',
                    clase_parrafo
                  ]">
                    {{ parrafo }}
                  </p>
                </slot>
              </div>
            </div>
          </div>

          <!-- PANEL DERECHO: MEDIA - Mobile: overflow-hidden (recorta) - Desktop: overflow-visible (permite cruzar al panel rojo) -->
          <div :class="[
            // z-10 para asegurar superposición por encima del panel texto
            'relative z-10 bg-[#bf2b27]',
            // Mobile: altura base para calzar el mockup
            'min-h-[420px]',
            // CLAVE: recorta en mobile / permite en desktop
            'overflow-hidden md:overflow-visible md:h-full',
            // Hook para hover
            'sis_media_group',
            clase_panel_media
          ]">
            <!-- Fondo desktop -->
            <div class="hidden md:block absolute inset-0 z-0 bg-no-repeat" :class="[clase_fondo_media_desktop]"
              :style="estilo_fondo_desktop" aria-hidden="true" />

            <!-- Fondo mobile -->
            <div class="md:hidden absolute inset-0 z-0 bg-no-repeat" :class="[clase_fondo_media_mobile]"
              :style="estilo_fondo_mobile" aria-hidden="true" />

            <!-- Imagen superpuesta (desktop)
                 - Puede cruzar al panel rojo con clases/estilos (ej: left:-6%)
                 - NO se sale del card porque el root recorta por overflow-hidden.
            -->
            <img v-if="imagen_superpuesta_desktop_efectiva" class="hidden md:block absolute z-10 select-none sis_img"
              :class="[
                // Defaults (si no pasas nada): centrado vertical en el panel media
                'right-0 top-1/2 w-[100%] h-auto object-contain',
                clase_img_superpuesta_desktop_compuesta
              ]" :style="estilo_img_superpuesta_desktop" :src="imagen_superpuesta_desktop_efectiva"
              :alt="alt_imagen_superpuesta_efectiva" loading="lazy" decoding="async" draggable="false" />

            <!-- Imagen superpuesta (mobile)
                 - Siempre dentro del panel media (abajo).
                 - Nunca invade el texto gracias al overflow-hidden del panel.
            -->
            <div v-if="imagen_superpuesta_mobile_efectiva"
              class="md:hidden absolute inset-0 z-10 flex items-end justify-center pointer-events-none select-none"
              aria-hidden="true">
              <img class="sis_img w-[112%] max-w-none h-[92%] object-contain"
                :class="[clase_img_superpuesta_mobile_compuesta]" :style="estilo_img_superpuesta_mobile"
                :src="imagen_superpuesta_mobile_efectiva" :alt="alt_imagen_superpuesta_efectiva" loading="lazy"
                decoding="async" draggable="false" />
            </div>

            <!-- Anotaciones (opcional) -->
            <svg v-if="anotaciones_normalizadas.length" class="absolute inset-0 z-20 w-full h-full"
              viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <template v-for="(a, i) in anotaciones_normalizadas" :key="i">
                <circle :cx="a.punto_x" :cy="a.punto_y" r="1.1" fill="#D11F1F" opacity="0.95" />
                <line :x1="a.punto_x" :y1="a.punto_y" :x2="a.linea_x" :y2="a.linea_y" stroke="rgba(255,255,255,0.70)"
                  stroke-width="0.6" stroke-dasharray="2 2" />
                <text :x="a.label_x" :y="a.label_y" fill="rgba(255,255,255,0.95)" font-size="3.0" font-weight="600"
                  style="letter-spacing:0.6px;">
                  {{ a.texto }}
                </text>
              </template>
            </svg>

            <div class="absolute inset-0 z-30">
              <slot name="overlay_media" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  /* Contenido */
  titulo: { type: String, required: true },
  parrafos: { type: Array, required: false, default: () => [] },

  /* Accesibilidad */
  aria_label: { type: String, required: false, default: '' },

  /* Layout */
  usar_contenedor: { type: Boolean, required: false, default: true },
  alto_desktop: { type: String, required: false, default: '466px' },
  alto_mobile: { type: String, required: false, default: 'auto' },
  radio_borde: { type: Number, required: false, default: 28 },

  /* Props nuevas */
  imagen_fondo_texto: { type: String, required: false, default: '' },
  imagen_fondo_desktop: { type: String, required: false, default: '' },
  imagen_fondo_mobile: { type: String, required: false, default: '' },

  imagen_superpuesta_desktop: { type: String, required: false, default: '' },
  imagen_superpuesta_mobile: { type: String, required: false, default: '' },
  alt_imagen_superpuesta: { type: String, required: false, default: '' },

  // Puede venir con transform desde el padre
  imagen_superpuesta_estilo_desktop: { type: Object, required: false, default: null },
  imagen_superpuesta_estilo_mobile: { type: Object, required: false, default: null },

  /* Props legacy */
  fondo_izquierdo_url: { type: String, required: false, default: '' },
  fondo_derecho_url: { type: String, required: false, default: '' },

  imagen_superpuesta_url: { type: String, required: false, default: '' },
  imagen_superpuesta_alt: { type: String, required: false, default: '' },
  imagen_superpuesta_clases: { type: String, required: false, default: '' },

  anotaciones: { type: Array, required: false, default: () => [] },

  /* Clases custom */
  clase_seccion: { type: String, required: false, default: '' },
  clase_contenedor: { type: String, required: false, default: '' },
  clase_grid: { type: String, required: false, default: '' },

  clase_panel_texto: { type: String, required: false, default: '' },
  clase_titulo: { type: String, required: false, default: '' },
  clase_contenido_texto: { type: String, required: false, default: '' },
  clase_parrafo: { type: String, required: false, default: '' },

  clase_panel_media: { type: String, required: false, default: '' },
  clase_fondo_media_desktop: { type: String, required: false, default: 'bg-cover bg-center' },
  clase_fondo_media_mobile: { type: String, required: false, default: 'bg-cover bg-center' },

  clase_img_superpuesta_desktop_extra: { type: String, required: false, default: '' },
  clase_img_superpuesta_mobile_extra: { type: String, required: false, default: '' }
})

/* Accesibilidad */
const aria_label_efectivo = computed(() => {
  const limpio = (props.aria_label || '').trim()
  return limpio || `Bloque informativo: ${props.titulo || 'contenido'}`
})

/* Normalización de párrafos */
const parrafos_normalizados = computed(() => {
  const lista = Array.isArray(props.parrafos) ? props.parrafos : []
  return lista.map(v => (v ?? '').toString().trim()).filter(v => v.length > 0)
})

/* Root style (alto + radio) */
const estilo_root = computed(() => ({
  height: props.alto_mobile,
  borderRadius: `${Math.max(0, props.radio_borde)}px`,
  overflow: 'hidden',
  '--alto-desktop': props.alto_desktop
}))

/* Fondo panel texto */
const imagen_fondo_texto_efectiva = computed(() => props.imagen_fondo_texto || props.fondo_izquierdo_url || '')
const estilo_fondo_texto = computed(() => ({
  backgroundImage: imagen_fondo_texto_efectiva.value ? `url('${imagen_fondo_texto_efectiva.value}')` : 'none'
}))

/* Triángulo */
const estilo_triangulo = computed(() => ({
  borderTop: '8px solid transparent',
  borderBottom: '8px solid transparent',
  borderLeft: '12px solid rgba(255,255,255,0.95)'
}))

/* Fondo media */
const imagen_fondo_desktop_efectiva = computed(() => props.imagen_fondo_desktop || props.fondo_derecho_url || '')
const imagen_fondo_mobile_efectiva = computed(() => props.imagen_fondo_mobile || props.fondo_derecho_url || '')

const estilo_fondo_desktop = computed(() => ({
  backgroundImage: imagen_fondo_desktop_efectiva.value ? `url('${imagen_fondo_desktop_efectiva.value}')` : 'none'
}))
const estilo_fondo_mobile = computed(() => ({
  backgroundImage: imagen_fondo_mobile_efectiva.value ? `url('${imagen_fondo_mobile_efectiva.value}')` : 'none'
}))

/* Imagen superpuesta */
const imagen_superpuesta_desktop_efectiva = computed(() => props.imagen_superpuesta_desktop || props.imagen_superpuesta_url || '')
const imagen_superpuesta_mobile_efectiva = computed(() => props.imagen_superpuesta_mobile || props.imagen_superpuesta_url || '')
const alt_imagen_superpuesta_efectiva = computed(() => props.alt_imagen_superpuesta || props.imagen_superpuesta_alt || '')

/**
 * Hover robusto aunque el padre mande `transform` inline:
 * - Si el padre manda `transform`, lo sacamos del inline y lo guardamos en `--sis_transform_base`
 * - Luego componemos: transform = base + scale(variable)
 * Así el hover nunca “muere” por prioridad de inline styles.
 */
function normalizar_estilo_img(estilo, transform_por_defecto) {
  const base = (estilo && typeof estilo === 'object') ? { ...estilo } : {}

  let t = ''
  if (typeof base.transform === 'string') {
    t = base.transform.trim()
    delete base.transform
  }

  // Evita doble scale si alguien manda scale(...) desde fuera
  if (t) {
    t = t.replace(/scale[XY]?\([^)]*\)/g, '').replace(/\s+/g, ' ').trim()
  }

  return {
    ...base,
    '--sis_transform_base': t || transform_por_defecto
  }
}

const estilo_img_superpuesta_desktop = computed(() =>
  normalizar_estilo_img(props.imagen_superpuesta_estilo_desktop, 'translateY(-50%)')
)

const estilo_img_superpuesta_mobile = computed(() =>
  normalizar_estilo_img(props.imagen_superpuesta_estilo_mobile, 'translate3d(0,0,0)')
)

/* Clases compuestas */
const clase_img_superpuesta_desktop_compuesta = computed(() => {
  const extra = (props.clase_img_superpuesta_desktop_extra || '').trim()
  const legacy = (props.imagen_superpuesta_clases || '').trim()
  return [extra, legacy].filter(Boolean).join(' ')
})

const clase_img_superpuesta_mobile_compuesta = computed(() => {
  const extra = (props.clase_img_superpuesta_mobile_extra || '').trim()
  // En mobile NO aplico legacy por defecto (normalmente venía pensado para absolute/desktop)
  return [extra].filter(Boolean).join(' ')
})

/* Anotaciones */
const anotaciones_normalizadas = computed(() => {
  const lista = Array.isArray(props.anotaciones) ? props.anotaciones : []
  return lista
    .map(a => ({
      texto: (a?.texto ?? '').toString(),
      punto_x: Number(a?.punto_x ?? 0),
      punto_y: Number(a?.punto_y ?? 0),
      linea_x: Number(a?.linea_x ?? 0),
      linea_y: Number(a?.linea_y ?? 0),
      label_x: Number(a?.label_x ?? 0),
      label_y: Number(a?.label_y ?? 0)
    }))
    .filter(a => a.texto.trim().length > 0)
})
</script>

<style scoped>
@media (min-width: 768px) {
  .seccion_informativa_split__root {
    height: var(--alto-desktop);
  }
}

/* Transform compuesto (base + scale) para que el hover funcione aunque el padre mande transform */
.sis_img {
  --sis_scale: 1;
  transform: var(--sis_transform_base) scale(var(--sis_scale));
  transform-origin: center;
  transition: transform 1s ease;
  will-change: transform;
}

/* Hover sobre el panel media (desktop) */
.sis_media_group:hover .sis_img {
  --sis_scale: 1.1;
}

/* Hover directo sobre la imagen */
.sis_img:hover {
  --sis_scale: 1.1;
}
</style>
